import classNames from 'classnames';
import React, { useMemo } from 'react';
import { MultiValue } from 'react-select';
import { FilterSelectOption } from '../../../../atomic/molecules/FlairDropdown/types';
import { FlairMultiSelectDropdown } from '../../../../atomic/molecules/FlairDropdown/FlairMultiSelectDropdown/FlairMultiSelectDropdown';
import { useNamespacedTranslation } from '../../../../hooks/useNamespacedTranslation';
import { useObjectLabels } from '../../hooks/useObjectLabels';
import {
  extractDepartmentOptions,
  extractLocationOptions,
  extractTeamOptions,
} from '../../utils/employeeFilterOptions';
import { OrgChartEmployee } from './types';
import { OrgChartFilters as OrgChartFiltersType } from './useOrgChartFilters';

type Props = {
  employees: OrgChartEmployee[];
  filters: OrgChartFiltersType;
  onFiltersChange: (filters: OrgChartFiltersType) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
};

const i18Path = 'people.orgChart';

const OrgChartFilters: React.FC<React.PropsWithChildren<Props>> = ({
  employees,
  filters,
  onFiltersChange,
  hasActiveFilters,
  onClearFilters,
}) => {
  const t = useNamespacedTranslation(i18Path);
  const objectLabels = useObjectLabels();

  const filterOptionsInput = useMemo(
    () =>
      employees.map((e) => ({
        location: e.locationRef
          ? {
              Id: e.locationRef.id,
              Name: e.locationRef.name,
              flair__Active__c: e.locationRef.active,
            }
          : null,
        department: e.departmentRef
          ? {
              Id: e.departmentRef.id,
              Name: e.departmentRef.name,
              flair__Active__c: e.departmentRef.active,
            }
          : null,
        teams: e.teams.map((team) => ({ Id: team.id, Name: team.name })),
      })),
    [employees],
  );

  const departmentOptions = useMemo(
    () => extractDepartmentOptions(filterOptionsInput),
    [filterOptionsInput],
  );

  const locationOptions = useMemo(
    () => extractLocationOptions(filterOptionsInput),
    [filterOptionsInput],
  );

  const teamOptions = useMemo(
    () => extractTeamOptions(filterOptionsInput),
    [filterOptionsInput],
  );

  const handleMultiSelectChange =
    (key: 'departments' | 'locations' | 'teams') =>
    (selected: MultiValue<FilterSelectOption>) => {
      onFiltersChange({
        ...filters,
        [key]: selected.map((o) => o.value),
      });
    };

  return (
    <div
      className={classNames('d-flex', 'align-items-center', 'gap-3', 'small')}>
      <span className="text-nowrap">{t('filterBy')}</span>
      <FlairMultiSelectDropdown
        options={departmentOptions}
        selectedOptions={filters.departments}
        onChange={handleMultiSelectChange('departments')}
        placeholder={objectLabels.department}
      />
      <FlairMultiSelectDropdown
        options={locationOptions}
        selectedOptions={filters.locations}
        onChange={handleMultiSelectChange('locations')}
        placeholder={objectLabels.location}
      />
      {teamOptions.length > 0 && (
        <FlairMultiSelectDropdown
          options={teamOptions}
          selectedOptions={filters.teams}
          onChange={handleMultiSelectChange('teams')}
          placeholder={t('team')}
        />
      )}
      <input
        type="text"
        className="form-control form-control-sm"
        placeholder={t('position')}
        value={filters.position}
        onChange={(e) =>
          onFiltersChange({ ...filters, position: e.target.value })
        }
        style={{ maxWidth: '10rem' }}
      />
      {hasActiveFilters && (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <a
          href=""
          className="text-nowrap"
          onClick={(e) => {
            e.preventDefault();
            onClearFilters();
          }}>
          {t('clearFilters')}
        </a>
      )}
    </div>
  );
};

export default OrgChartFilters;
